/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react/destructuring-assignment */
import { CSSProperties, ReactNode, useCallback, useState } from 'react';
import Select, { ActionMeta, components, InputProps, SelectComponentsConfig } from 'react-select';
import AsyncSelect from 'react-select/async';
import type { MultiValueGenericProps, MultiValueRemoveProps } from 'react-select/src/components/MultiValue';
import type { CommonProps } from 'react-select/src/types';
import cn from 'classnames';

import { ErknmFillInProcessMessage } from 'KnmErknmPackage/MainModule/components/ErknmFillInProcessMessage';
import { IInspectionResult } from 'APIPackage/CatalogsModule/interfaces/IInspectionResult';
import { ErknmInspectionResultMessage } from 'KnmErknmPackage/MainModule/components/ErknmInspectionResultMessageMessage';
import { InspectionResultActionTypeEnum } from 'APIPackage/CatalogsModule/enums/InspectionResultActionTypeEnum';
import { Hint } from 'CorePackage/MainModule/components/Hint';

import styles from './SelectInput.module.scss';

type IOptionType = any;
type ISelectComponents = SelectComponentsConfig<IOptionType, boolean>;

const Control: ISelectComponents['Control'] = props => {
  const { isDisabled, menuIsOpen } = props.selectProps;
  const controlClassName = cn(styles.Control, {
    [styles.ControlMenuIsOpen]: menuIsOpen,
    [styles.ControlDisabled]: isDisabled,
  });

  return <components.Control className={controlClassName} {...props} />;
};

const ValueContainer: ISelectComponents['ValueContainer'] = props => {
  const { isMulti } = props.selectProps;
  const valueContainerClassName = cn(styles.ValueContainer, {
    [styles.ValueContainerSingle]: !isMulti,
    [styles.ValueContainerMulti]: isMulti,
  });

  return <components.ValueContainer className={valueContainerClassName} {...props} />;
};

const getComponentSingleValue = ({ isMultiLine }: ISelectInputProps) => {
  const SingleValue: ISelectComponents['SingleValue'] = props => {
    const { value, menuIsOpen } = props.selectProps;
    const singleValueClassName = cn(styles.SingleValue, {
      [styles.SingleValueSingleLine]: !isMultiLine && !menuIsOpen,
      [styles.SingleValueInputHidden]: value && !menuIsOpen,
    });

    return <components.SingleValue className={singleValueClassName} {...props} />;
  };

  return SingleValue;
};

const MultiValueContainer = (props: MultiValueGenericProps<IOptionType>) => {
  // eslint-disable-next-line no-param-reassign
  props.innerProps.className = cn(props.innerProps.className, styles.MultiValue, {
    [styles.MultiValueDisabled]: props.selectProps.isDisabled,
  });

  return <components.MultiValueContainer {...props} />;
};

const MultiValueLabel = (props: MultiValueGenericProps<IOptionType>) => {
  // eslint-disable-next-line no-param-reassign
  props.innerProps.className = cn(props.innerProps.className, styles.MultiValueLabel);

  return <components.MultiValueLabel {...props} />;
};

const MultiValueRemove = (props: MultiValueRemoveProps<IOptionType>) => {
  // eslint-disable-next-line no-param-reassign
  props.innerProps.className = cn(props.innerProps.className, styles.MultiValueRemove, {
    [styles.MultiValueRemoveDisabled]: props.selectProps.isDisabled,
  });

  return <components.MultiValueRemove {...props} />;
};

const IndicatorsContainer: ISelectComponents['IndicatorsContainer'] = props => (
  <components.IndicatorsContainer className={styles.Indicators} {...props} />
);

const Input = (props: CommonProps<IOptionType, boolean> & InputProps) => {
  const { menuIsOpen } = props.selectProps;
  const inputClassName = cn(styles.Input, { [styles.InputHidden]: !menuIsOpen });

  return <components.Input className={inputClassName} {...props} />;
};

const CustomOption: ISelectComponents['Option'] = props => {
  const { isDisabled, isFocused, isSelected } = props;

  return (
    <components.Option
      className={cn(styles.Option, {
        [styles.OptionDisabled]: isDisabled,
        [styles.OptionFocused]: isFocused,
        [styles.OptionSelected]: isSelected,
      })}
      {...props}
    />
  );
};

const NoOptionsMessage: ISelectComponents['NoOptionsMessage'] = props => (
  <components.NoOptionsMessage className={styles.NoOptionsMessage} {...props} />
);

const onInputChangeNull = () => null;

export interface ISelectOption<T = string | number> {
  id: T;
  value?: any;
  label: string;
  enabled?: boolean | null; // ToDo: rename to "isEnabled"
  disabled?: boolean;
  isDisabled?: boolean;
  byDefault?: boolean;
  inspectionResult?: IInspectionResult;
}

export interface ISelectInputProps {
  options?: ISelectOption[];
  className?: string;
  id?: string;
  name?: string;
  value?: null | ISelectOption | ISelectOption[];
  defaultValue?: null | ISelectOption | ISelectOption[];
  placeholder?: ReactNode;
  label?: string;
  size?: 'medium' | 'large';
  loadingMessage?: string;
  noOptionsMessage?: string;
  disabled?: boolean;
  invalid?: boolean;
  error?: string;
  isMultiLine?: boolean;
  isSearchable?: boolean;
  isMulti?: boolean;
  isClearable?: boolean;
  isAsync?: boolean;
  cacheOptions?: boolean | string;
  opensOnTop?: boolean;
  validateOnSubmit?: boolean;
  defaultOptions?: ISelectOption[] | boolean;
  isDeepLoading?: boolean;
  loadOptions?: (inputValue: string, callback?: (options: ISelectOption[]) => void) => Promise<any> | void;
  onChange?: (value: any, actionMeta: ActionMeta<ISelectOption>) => void;
  onClear?: () => void;
  onOpen?: () => void;
  menuPortalTarget?: HTMLElement | null;
  getOptionValue?: (v: ISelectOption) => string;
  warning?: string;
}

export const initialSelectInputData = {
  id: '',
  value: '',
  label: '',
};

export const SelectInput = ({
  className,
  id,
  placeholder = '',
  options,
  defaultValue = null,
  value = null,
  label,
  size = 'large',
  loadingMessage = 'Загрузка...',
  noOptionsMessage = 'Значение отсутствует',
  disabled,
  invalid,
  error,
  isMultiLine = true,
  isSearchable = false,
  isClearable = true,
  isAsync = false,
  defaultOptions,
  loadOptions,
  opensOnTop = false,
  onClear,
  onChange,
  onOpen,
  isDeepLoading,
  cacheOptions = true,
  warning,
  ...rest
}: ISelectInputProps) => {
  const [menuOpen, setMenuOpen] = useState<boolean>(false);

  const onMenuOpen = useCallback(() => {
    onOpen?.();
    setMenuOpen(true);
  }, [onOpen]);

  const onMenuClose = useCallback(() => setMenuOpen(false), []);

  let inspectionResult: IInspectionResult | undefined;

  if (!Array.isArray(value) && value && value.inspectionResult) {
    inspectionResult = value.inspectionResult;
  }

  const classNames = cn([
    styles.SelectInput,
    className,
    {
      [styles.SelectInputSizeMedium]: size === 'medium',
      [styles.SelectInputSizeLarge]: size === 'large',
      [styles.SelectInputNotSearchable]: !isSearchable,
      [styles.SelectInputSearchable]: isSearchable,
      [styles.SelectInputInvalid]: invalid,
      [styles.SelectInputDisabled]: disabled,
      [styles.SelectInputOpensOnTop]: opensOnTop && menuOpen,
      [styles.SelectInputInspectionResultYellow]:
        inspectionResult && inspectionResult.resultActionType === InspectionResultActionTypeEnum.LIGHT_YELLOW,
      [styles.SelectInputInspectionResultRed]:
        inspectionResult &&
        (inspectionResult.resultActionType === InspectionResultActionTypeEnum.LIGHT_RED ||
          inspectionResult.resultActionType === InspectionResultActionTypeEnum.BLOCK_KNM),
    },
  ]);

  const selectContainerClassName = cn([
    styles.SelectContainer,
    'select-field-container',
    {
      'select-field-container--size-medium': size === 'medium',
      'select-field-container--size-large': size === 'large',
    },
  ]);

  // reset default styles
  const customStyles = {
    container: () => ({}),
    control: () => ({}),
    indicatorsContainer: () => ({}),
    menu: () => ({}),
    multiValue: () => ({}),
    multiValueLabel: () => ({}),
    multiValueRemove: () => ({}),
    noOptionsMessage: () => ({}),
    option: () => ({}),
    singleValue: () => ({}),
    valueContainer: () => ({}),
    menuPortal: (base: CSSProperties) => ({ ...base, zIndex: document.getElementById('modal') ? 1001 : 999 }),
  };

  const SingleValue = getComponentSingleValue({ isMultiLine });
  const restyledComponents = {
    Control,
    IndicatorsContainer,
    Input,
    MultiValueContainer,
    MultiValueLabel,
    MultiValueRemove,
    NoOptionsMessage,
    Option: CustomOption,
    SingleValue,
    ValueContainer,
  };
  const getLoadingMessage = useCallback(() => loadingMessage, [loadingMessage]);
  const getNoOptionMessage = useCallback(() => noOptionsMessage, [noOptionsMessage]);

  return (
    <div className={classNames}>
      {label && (
        <label className={styles.Label} htmlFor={id}>
          {label}
        </label>
      )}
      {isAsync && (
        <AsyncSelect
          className={selectContainerClassName}
          classNamePrefix="select-field"
          components={restyledComponents}
          styles={customStyles}
          id={id}
          loadingMessage={getLoadingMessage}
          noOptionsMessage={getNoOptionMessage}
          maxMenuHeight={200}
          hideSelectedOptions={false}
          loadOptions={loadOptions!}
          placeholder={placeholder}
          isSearchable={isSearchable}
          isClearable={isClearable}
          cacheOptions={cacheOptions}
          defaultOptions={defaultOptions}
          value={value ?? defaultValue ?? ''}
          isDisabled={disabled}
          onChange={disabled ? undefined : onChange}
          {...rest}
        />
      )}
      {!isAsync && (
        <Select
          className={selectContainerClassName}
          classNamePrefix="select-field"
          components={restyledComponents}
          styles={customStyles}
          id={id}
          noOptionsMessage={getNoOptionMessage}
          maxMenuHeight={200}
          hideSelectedOptions={false}
          options={options}
          defaultValue={defaultValue}
          placeholder={placeholder}
          isSearchable={isSearchable}
          isDisabled={disabled}
          isClearable={onClear ? false : isClearable}
          onMenuOpen={onMenuOpen}
          onMenuClose={onMenuClose}
          value={value ?? defaultValue ?? ''}
          onChange={disabled ? undefined : onChange}
          onInputChange={disabled ? onInputChangeNull : undefined}
          {...rest}
        />
      )}
      {isClearable && onClear && <button className={styles.ClearButton} type="button" onClick={onClear} hidden />}
      {invalid && error && typeof error === 'string' && <div className={styles.SelectInputError}>{error}</div>}
      {!invalid && !error && <ErknmFillInProcessMessage name={rest.name} />}
      {inspectionResult && <ErknmInspectionResultMessage inspectionResult={inspectionResult} />}
      {warning && (
        <div className={styles.Warning}>
          <Hint text={warning} />
        </div>
      )}
    </div>
  );
};
